import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import appConfig from "../../../../config/appConfig";
import { Link } from "react-router-dom";
import { ToastBottomEnd } from "../../../../views/components/Toast";
import {
  toastSuccessMessageWithParameterConfig,
  toastErrorMessageWithParameterConfig,
} from "../../../../config/toastConfig";
import "./LoginCss.css";
import Loader from "../../../../views/components/Loader";
import { useTranslation } from "react-i18next";

const Verify2FA = () => {
  const { t, ready } = useTranslation();

  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Timer State (in seconds)
  const [timer, setTimer] = useState(-1); 

  const inputRefs = useRef([]);

  // Countdown Logic
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timer]);


  useEffect(() => {
    // Start with a 2-minute timer on component mount
    axios.get(`${appConfig.apiDomaine}/2fa/remaining-time`)
      .then((response) => {
        setTimer(response.data.retry_after);
      })
      .catch((error) => {
        ToastBottomEnd.fire(
          toastErrorMessageWithParameterConfig(error.response?.data?.message || t("Erreur lors de la récupération du temps restant"))
        );
      });
  }, []);

  // Format seconds to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleResend = (e) => {
    e.preventDefault();
    if (timer > 0) return;

    setLoading(true);
    axios
      .post(`${appConfig.apiDomaine}/2fa/resend`)
      .then((response) => {
        setLoading(false);
        setTimer(response.data.retry_after); // Reset timer to 2 minutes
        setOtp(new Array(6).fill("")); // Clear OTP inputs
        inputRefs.current[0].focus(); // Focus on the first input
        ToastBottomEnd.fire(
          toastSuccessMessageWithParameterConfig(response?.data?.message || t("Code renvoyé avec succès"))
        );
      })
      .catch((error) => {
        setLoading(false);
        if (error.response && error.response.status === 429) {
          // Sync timer with backend if out of sync
          setTimer(error.response.data.retry_after);
        }
        ToastBottomEnd.fire(
          toastErrorMessageWithParameterConfig(error.response?.data?.message || t("Erreur lors de l'envoi")),
        );
      });
  };

  // ... (Keep handleChange, handleKeyDown, handlePaste from your original code)
  const handleChange = (element, index) => {
    if (isNaN(element.value)) return false;
    const newOtp = [...otp];
    newOtp[index] = element.value.substring(element.value.length - 1);
    setOtp(newOtp);
    if (element.value && index < 5) inputRefs.current[index + 1].focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) inputRefs.current[index - 1].focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalOtp = otp.join("");
    if (finalOtp.length < 6) {
      ToastBottomEnd.fire(
        toastErrorMessageWithParameterConfig(t("Veuillez entrer un code de 6 chiffres"))
      );
      return;
    }
    setIsSubmitting(true);
    axios.post(`${appConfig.apiDomaine}/2fa/verify`, { otp: finalOtp })
      .then((response) => {
        setIsSubmitting(false);
        ToastBottomEnd.fire(
          toastSuccessMessageWithParameterConfig(response?.data?.message || t("Vérification 2FA réussie"))
        );
        window.location.href = "/dashboard";
      })
      .catch((error) => {
        console.log(error);
        
        setIsSubmitting(false);
        ToastBottomEnd.fire(
          toastErrorMessageWithParameterConfig(error.response?.data?.message || t("Code 2FA invalide"))
        );
        if(error.response.data.retry_after) {
          setTimer(error?.response?.data?.retry_after);
        }

      });
  };

  return !loading ? (
    <div className="kt-login__form" style={{ paddingTop: "15vh", margin: "0 auto", maxWidth: "450px" }}>
      <div className="kt-login__head text-center">
        <h3 className="kt-login__title">{t("Vérification 2FA")}</h3>
        <div className="kt-login__desc">
          {t("Entrez le code à 6 chiffres envoyé à votre email")}
        </div>
      </div>

      <form className="kt-form" onSubmit={handleSubmit} style={{ marginTop: "30px" }}>
        <div className="otp-input-container">
          {otp.map((data, index) => (
            <input
              key={index}
              type="text"
              maxLength="1"
              className="otp-field"
              value={data}
              ref={(el) => (inputRefs.current[index] = el)}
              onChange={(e) => handleChange(e.target, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
            />
          ))}
        </div>

        <div className="text-center" style={{ marginTop: "20px" }}>
            {timer > 0 ? (
                <p className="text-muted">
                    {t("Renvoyer le code dans")} <strong>{formatTime(timer)}</strong>
                </p>
            ) : timer === 0 && (
                <button 
                    type="button" 
                    className="btn btn-link p-2" 
                    onClick={handleResend}
                    style={{ textDecoration: 'none', fontWeight: 'bold' }}
                >
                    {t("Renvoyer le code maintenant")}
                </button>
            )}
        </div>

        <div className="kt-login__actions" style={{ marginTop: "30px", display: "flex", justifyContent: "center", gap: "10px" }}>
          <button type="submit" className={`btn btn-brand btn-pill ${isSubmitting ? 'kt-spinner kt-spinner--right kt-spinner--light' : ''}`} disabled={isSubmitting}>
            {t("Vérifier")}
          </button>
          <Link to="/login" className="btn btn-outline-brand btn-pill">
            {t("Annuler")}
          </Link>
        </div>
      </form>
    </div>
  ) : (
    <Loader />
  );
};

export default Verify2FA;